import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Input, Select, Typography } from 'antd';
import { attachBookToSeries, getSeries, listProjects } from '../illustration/api';
import { downloadUrlForStoragePath } from '../illustration/cloudProjects';
import { writesStayLocal } from '../illustration/localOnly';
import { shelfDisplayName, shelfPicture, shelfStoragePath } from './shelfPicture';
import './SeriesPage.css';

const { Title, Paragraph, Text } = Typography;

function volumeNumber(series, projectId) {
  const value = Number(series?.volumes?.[projectId]);
  return Number.isInteger(value) && value > 0 ? value : null;
}

export default function SeriesPage() {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [projects, setProjects] = useState([]);
  const [volume, setVolume] = useState('');
  const [error, setError] = useState('');
  const [attaching, setAttaching] = useState(false);
  const [pictures, setPictures] = useState({});

  const reload = useCallback(async () => {
    const [data, listed] = await Promise.all([getSeries(seriesId), listProjects()]);
    setSeries(data.series);
    setProjects(listed.projects || []);
  }, [seriesId]);

  useEffect(() => {
    let cancel = false;
    reload().catch((err) => {
      if (!cancel) setError(err.message);
    });
    return () => {
      cancel = true;
    };
  }, [reload]);

  useEffect(() => {
    if (!series || writesStayLocal()) return undefined;
    let cancel = false;
    Promise.all((series.characters || []).map(async (card) => {
      const storagePath = shelfStoragePath(series.id, card);
      const url = storagePath ? await downloadUrlForStoragePath(storagePath) : '';
      return [card.id, url];
    })).then((rows) => {
      if (!cancel) setPictures(Object.fromEntries(rows));
    });
    return () => {
      cancel = true;
    };
  }, [series]);

  const booksOnShelf = projects
    .filter((project) => (series?.bookIds || []).includes(project.id))
    .sort((left, right) => {
      const a = volumeNumber(series, left.id);
      const b = volumeNumber(series, right.id);
      if (a && b && a !== b) return a - b;
      if (a && !b) return -1;
      if (!a && b) return 1;
      return 0;
    });
  const booksYouCanAdd = projects.filter((project) => !project.seriesId);

  async function addBook(nextProjectId) {
    setError('');
    setAttaching(true);
    try {
      const n = Number(volume);
      await attachBookToSeries(seriesId, nextProjectId, Number.isInteger(n) && n > 0 ? n : undefined);
      navigate(`/series/${seriesId}/add/${nextProjectId}`);
    } catch (err) {
      setError(err.message);
      setAttaching(false);
    }
  }

  if (!series) return error ? <Alert type="error" showIcon title={error} /> : null;

  const people = series.characters || [];

  return (
    <div className="workflow-shell__content">
      <Title level={1} style={{ fontFamily: 'var(--font-display)', marginTop: 0 }}>{series.title}</Title>
      <Paragraph style={{ maxWidth: 'var(--measure-prose)', color: 'var(--color-text-secondary)' }}>
        Books sit on the top shelf. People stand on the shelf below.
      </Paragraph>
      {error ? <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} /> : null}

      <p className="series-label">Books</p>
      <div className="series-shelf">
        <Link className="series-add" to={`/new?series=${encodeURIComponent(seriesId)}`}>
          <strong>New book</strong>
          <span>Title, subtitle, Vol</span>
        </Link>
        {booksOnShelf.map((project) => {
          const vol = volumeNumber(series, project.id);
          return (
            <figure className="series-book" key={project.id}>
              <Link className="series-book__cover" to={`/projects/${project.id}/characters`}>
                <strong>{vol ? `Vol ${vol}` : project.title}</strong>
                {project.subtitle ? <span>{project.subtitle}</span> : null}
              </Link>
              <figcaption>{series.title}</figcaption>
            </figure>
          );
        })}
      </div>
      <div className="series-shelf__edge" />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: -8, marginBottom: 28 }}>
        <Input
          aria-label="Volume number"
          inputMode="numeric"
          placeholder="Vol"
          value={volume}
          onChange={(event) => setVolume(event.target.value)}
          style={{ width: 88 }}
        />
        <Select
          style={{ minWidth: 240 }}
          placeholder="Add a book that already exists"
          loading={attaching}
          value={undefined}
          onChange={addBook}
          options={booksYouCanAdd.map((project) => ({ value: project.id, label: project.title }))}
          notFoundContent="Every book is already on a series."
        />
      </div>

      <p className="series-label">Shelf</p>
      <div className="series-shelf">
        {people.map((card) => {
          const picture = pictures[card.id] || shelfPicture(card);
          return (
            <figure className="series-person" key={card.id}>
              {picture ? (
                <img src={picture} alt="" />
              ) : (
                <span className="series-person__empty" aria-hidden="true" />
              )}
              <figcaption>{shelfDisplayName(card.name)}</figcaption>
            </figure>
          );
        })}
        {booksOnShelf.length === 1 ? (
          <Link className="series-add" to={`/series/${seriesId}/add/${booksOnShelf[0].id}`}>
            <strong>Add from a book</strong>
            <span>{booksOnShelf[0].title}</span>
          </Link>
        ) : null}
        {booksOnShelf.length > 1 ? (
          <div className="series-add series-add--inline">
            <strong>Add from a book</strong>
            <Select
              aria-label="Choose the book"
              placeholder="Choose the book"
              style={{ width: '100%', marginTop: 8 }}
              onChange={(projectId) => navigate(`/series/${seriesId}/add/${projectId}`)}
              options={booksOnShelf.map((project) => ({ value: project.id, label: project.title }))}
            />
          </div>
        ) : null}
        {!people.length && !booksOnShelf.length ? (
          <Text type="secondary">Add a book, then choose who stands here.</Text>
        ) : null}
      </div>
      <div className="series-shelf__edge" />
      {!people.length && booksOnShelf.length ? (
        <Paragraph style={{ maxWidth: 'var(--measure-prose)' }}>
          <Button type="primary" onClick={() => navigate(`/series/${seriesId}/add/${booksOnShelf[0].id}`)}>
            Choose who goes on the shelf
          </Button>
        </Paragraph>
      ) : null}
    </div>
  );
}
